import type { SnapshotIn } from "@gadgetinc/mobx-quick-tree";
import { differenceBy, isEqual } from "lodash";
import type {
  ShopifyConvergeActionPlanItem,
  ShopifyConvergeFieldPlanItem,
  ShopifyConvergeFilePlanItem,
  ShopifyConvergeModelPlanItem,
  ShopifyConvergePlanItem,
} from "../../ShopifyConvergePlanTypes";
import type { Effect } from "../../database/behaviour/effects/Effect";
import { FieldType } from "../../type-system/FieldType";
import { unthunk } from "../../utils";
import type { ConnectionModelActionSpec, ConnectionModelFieldSpec, ConnectionModelSpec } from "../utils";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import { isModelSpecDisconnected, shopifyConnectionModelSpecsForVersion } from "./ShopifyModelMetadata";

type ConnectionModelSnapshot = {
  models: {
    key: string;
    name: string;
    hasMetafields: boolean;
    isDisconnected: boolean | undefined;
    replacedByModelKey: string | undefined;
    syncDisabled: boolean | undefined;
    addedRequiredScopes: string[] | undefined;
    fields: ConnectionModelFieldSpec[];
    actions: ConnectionModelActionSpec[];
    frameworkVersionConstraint: string;
  }[];
  files: { path: string }[];
};

function buildSchemaSnapshotFromConnectionModelSpecs(specs: ConnectionModelSpec[], frameworkVersionConstraint: string) {
  return {
    models: specs.map((spec) => ({
      key: spec.key,
      name: spec.name,
      hasMetafields: !!spec.hasMetafields,
      isDisconnected: isModelSpecDisconnected(spec, frameworkVersionConstraint) || undefined,
      replacedByModelKey: spec.replacedByModelKey,
      syncDisabled: spec.syncConfig.type === "webhook_only",
      // cast to avoid Deepreadonly readonly type errors
      addedRequiredScopes: spec.addedRequiredScopes ? [...spec.addedRequiredScopes] : undefined,
      // cast to avoid Deepreadonly readonly type errors
      fields: spec.fields as ConnectionModelFieldSpec[],
      actions: (spec.stateChart?.actions ?? []) as ConnectionModelActionSpec[],
      frameworkVersionConstraint,
    })),
    files: specs.flatMap((spec) => spec.files ?? []),
  };
}

/**
 *
 */
export class ShopifyConvergePlan {
  static buildPlanFromApiVersions(
    current: { apiVersion: ShopifyApiVersion; frameworkVersionConstraint: string },
    target: { apiVersion: ShopifyApiVersion; frameworkVersionConstraint: string }
  ) {
    const currentSpecs = shopifyConnectionModelSpecsForVersion(current.apiVersion, current.frameworkVersionConstraint);
    const targetSpecs = shopifyConnectionModelSpecsForVersion(target.apiVersion, target.frameworkVersionConstraint);

    return ShopifyConvergePlan.buildPlanFromConnectionModelSpecs(
      { specs: currentSpecs, frameworkVersionConstraint: current.frameworkVersionConstraint },
      { specs: targetSpecs, frameworkVersionConstraint: target.frameworkVersionConstraint }
    );
  }

  static buildPlanFromConnectionModelSpecs(
    current: { specs: ConnectionModelSpec[]; frameworkVersionConstraint: string },
    target: { specs: ConnectionModelSpec[]; frameworkVersionConstraint: string }
  ) {
    const currentSnapshot = buildSchemaSnapshotFromConnectionModelSpecs(current.specs, current.frameworkVersionConstraint);
    const targetSnapshot = buildSchemaSnapshotFromConnectionModelSpecs(target.specs, target.frameworkVersionConstraint);

    return new ShopifyConvergePlan(currentSnapshot, targetSnapshot);
  }

  items: ShopifyConvergePlanItem[];

  constructor(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot) {
    this.items = this.buildPlanItems(currentSnapshot, targetSnapshot);
  }

  private buildPlanItems(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot): ShopifyConvergePlanItem[] {
    const fieldPlanItems: ShopifyConvergeFieldPlanItem[] = this.buildFieldPlanItems(currentSnapshot, targetSnapshot);
    const actionPlanItems: ShopifyConvergeActionPlanItem[] = this.buildActionPlanItems(currentSnapshot, targetSnapshot);
    const filePlanItems: ShopifyConvergeFilePlanItem[] = this.buildFilePlanItems(currentSnapshot, targetSnapshot);
    const modelPlanItems: ShopifyConvergeModelPlanItem[] = this.buildModelPlanItems(currentSnapshot, targetSnapshot);

    return [...fieldPlanItems, ...actionPlanItems, ...filePlanItems, ...modelPlanItems];
  }

  private buildModelPlanItems(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot) {
    const modelPlanItems: ShopifyConvergeModelPlanItem[] = [];
    for (const targetModel of targetSnapshot.models) {
      const currentModel = currentSnapshot.models.find((m) => m.key == targetModel.key);
      if (!currentModel) continue;

      if (!currentModel.hasMetafields && targetModel.hasMetafields) {
        modelPlanItems.push({
          type: "add_metafields",
          model: { key: targetModel.key, name: targetModel.name },
        });
      }

      if (!targetModel.isDisconnected && !currentModel.replacedByModelKey && targetModel.replacedByModelKey) {
        const replacement = targetSnapshot.models.find((model) => model.key === targetModel.replacedByModelKey);

        modelPlanItems.push({
          type: "deprecate_model",
          model: { key: targetModel.key, name: targetModel.name },
          ...(replacement ? { replacedBy: { key: replacement.key, name: replacement.name } } : {}),
        });
      }

      if (!currentModel.isDisconnected && targetModel.isDisconnected) {
        modelPlanItems.push({
          type: "disconnect_model",
          model: { key: currentModel.key, name: currentModel.name },
          fields: currentModel.fields.map((field) => ({
            type: "remove_field",
            model: { key: currentModel.key, name: targetModel.name },
            field: field,
          })),
        });
      }

      if (targetModel.addedRequiredScopes && !isEqual(currentModel.addedRequiredScopes, targetModel.addedRequiredScopes)) {
        modelPlanItems.push({
          type: "add_model_scope",
          model: { key: currentModel.key, name: currentModel.name },
          newScopes: targetModel.addedRequiredScopes,
        });
      }

      if (!currentModel.syncDisabled && targetModel.syncDisabled) {
        modelPlanItems.push({
          type: "sync_disabled",
          model: { key: currentModel.key, name: currentModel.name },
        });
      }
    }

    return modelPlanItems;
  }

  private buildFieldPlanItems(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot) {
    const fieldPlanItems: ShopifyConvergeFieldPlanItem[] = [];
    for (const targetModel of targetSnapshot.models) {
      const currentModel = currentSnapshot.models.find((m) => m.key == targetModel.key);
      if (!currentModel) continue;

      for (const targetField of targetModel.fields) {
        const currentField = findField(currentModel.fields, targetField);

        if (!currentField) {
          fieldPlanItems.push({
            type: "add_field",
            model: { key: targetModel.key, name: targetModel.name },
            field: targetField,
          });
          continue;
        }

        const targetIsDeleted = targetField.isDeleted ? unthunk(targetField.isDeleted, targetModel.frameworkVersionConstraint) : false;
        const currentIsDeleted = currentField.isDeleted ? unthunk(currentField.isDeleted, currentModel.frameworkVersionConstraint) : false;

        // isDeleted is a "is deleted from shopify but we'll keep it in Gadget" flow, disconnection will change dominion but keep the field
        if (targetIsDeleted && !currentIsDeleted) {
          fieldPlanItems.push({
            type: "disconnect_field",
            model: { key: targetModel.key, name: targetModel.name },
            field: targetField,
          });
        } else if (targetField.fieldType != currentField.fieldType) {
          fieldPlanItems.push({
            type: "change_field_type",
            model: { key: targetModel.key, name: targetModel.name },
            field: targetField,
            oldFieldType: currentField.fieldType,
            newFieldType: targetField.fieldType,
          });
        } else if (
          targetField.contributorConfiguration?.[ShopifyDominion.id]?.deprecated &&
          !currentField.contributorConfiguration?.[ShopifyDominion.id]?.deprecated
        ) {
          fieldPlanItems.push({
            type: "deprecate_field",
            model: { key: targetModel.key, name: targetModel.name },
            field: targetField,
          });
        } else if (targetField.fieldType == FieldType.Enum) {
          const targetOptions: { name: string; color?: string }[] = targetField.configuration?.options;
          if (!targetOptions) continue;

          // does not currently support removal of enum options
          const newEnumOptions = differenceBy(targetOptions, currentField.configuration?.options ?? [], "name");
          for (const newOption of newEnumOptions) {
            fieldPlanItems.push({
              type: "add_enum_option",
              model: { key: targetModel.key, name: targetModel.name },
              field: targetField,
              newOption,
            });
          }
        }
      }

      const removedFields = currentModel.fields.filter((f) => !findField(targetModel.fields, f));

      for (const removedField of removedFields) {
        fieldPlanItems.push({
          type: "remove_field",
          model: { key: currentModel.key, name: targetModel.name },
          field: removedField,
        });
      }
    }

    return fieldPlanItems;
  }

  private buildActionPlanItems(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot) {
    const actionPlanItems: ShopifyConvergeActionPlanItem[] = [];
    for (const targetModel of targetSnapshot.models) {
      const currentModel = currentSnapshot.models.find((m) => m.key == targetModel.key);
      if (!currentModel) continue;

      for (const targetAction of targetModel.actions) {
        const currentAction = findAction(currentModel.actions, targetAction);
        if (!currentAction) {
          actionPlanItems.push({
            type: "add_action",
            model: { key: targetModel.key, name: targetModel.name },
            action: targetAction,
          });
          continue;
        }

        const targetRunEffects = targetAction.onRunEffects ?? [];
        const currentRunEffects = currentAction.onRunEffects ?? [];

        for (const runEffect of targetRunEffects) {
          const currentEffect = findEffect(currentRunEffects, runEffect);
          if (!currentEffect) {
            actionPlanItems.push({
              type: "add_run_effect",
              model: { key: targetModel.key, name: targetModel.name },
              action: targetAction,
              effect: runEffect,
            });
            continue;
          }
        }

        const removedRunEffects = currentRunEffects.filter((e) => !findEffect(targetRunEffects, e));

        for (const removedRunEffect of removedRunEffects) {
          actionPlanItems.push({
            type: "remove_run_effect",
            model: { key: targetModel.key, name: targetModel.name },
            action: targetAction,
            effect: removedRunEffect,
          });
        }

        const targetSuccessEffects = targetAction.onSuccessEffects ?? [];
        const currentSucessEffects = currentAction.onSuccessEffects ?? [];

        for (const runEffect of targetSuccessEffects) {
          const currentEffect = findEffect(currentSucessEffects, runEffect);
          if (!currentEffect) {
            actionPlanItems.push({
              type: "add_success_effect",
              model: { key: targetModel.key, name: targetModel.name },
              action: targetAction,
              effect: runEffect,
            });
            continue;
          }
        }

        const removedSuccessEffects = currentSucessEffects.filter((e) => !findEffect(targetSuccessEffects, e));

        for (const removedRunEffect of removedSuccessEffects) {
          actionPlanItems.push({
            type: "remove_success_effect",
            model: { key: targetModel.key, name: targetModel.name },
            action: targetAction,
            effect: removedRunEffect,
          });
        }
      }

      const removedActions = currentModel.actions.filter((a) => !findAction(targetModel.actions, a));
      for (const removedAction of removedActions) {
        actionPlanItems.push({
          type: "remove_action",
          model: { key: currentModel.key, name: targetModel.name },
          action: removedAction,
        });
      }
    }
    return actionPlanItems;
  }

  private buildFilePlanItems(currentSnapshot: ConnectionModelSnapshot, targetSnapshot: ConnectionModelSnapshot) {
    const filePlanItems: ShopifyConvergeFilePlanItem[] = [];
    const currentFiles = currentSnapshot.files ?? [];
    const targetFiles = targetSnapshot.files ?? [];

    for (const targetFile of targetFiles) {
      const currentFile = findFile(currentFiles, targetFile);
      if (!currentFile) {
        filePlanItems.push({
          type: "add_file",
          file: targetFile,
        });
        continue;
      }
    }

    const removedFiles = currentFiles.filter((f) => !findFile(targetFiles, f));
    for (const removedFile of removedFiles) {
      filePlanItems.push({
        type: "remove_file",
        file: removedFile,
      });
    }

    return filePlanItems;
  }
}

function findFile(files: { path: string }[], file: { path: string }) {
  return files.find((f) => f.path == file.path);
}

function findAction(actions: ConnectionModelActionSpec[], action: ConnectionModelActionSpec) {
  return actions.find((a) => a.key == action.key || a.name == action.name);
}

function findEffect(effects: SnapshotIn<typeof Effect>[], effect: SnapshotIn<typeof Effect>) {
  return effects.find((e) => e.key == effect.key || e.specID == effect.specID);
}

function findField(fields: ConnectionModelFieldSpec[], field: ConnectionModelFieldSpec) {
  return fields.find(
    (f) =>
      (f.key && f.key == field.key) ||
      f.name == field.name ||
      (f.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey &&
        f.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey ==
          field.contributorConfiguration?.[ShopifyDominion.id]?.shopifyFieldKey) ||
      (f.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey &&
        f.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey ==
          field.contributorConfiguration?.[ShopifyDominion.id]?.graphQLFieldKey)
  );
}
